import { ROUTES } from '@/app/router/routes';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { ActionIcon } from '@/components/ui/ActionIcon';
import {
  TABLE,
  TABLE_BODY,
  TABLE_CELL,
  TABLE_CELL_TEXT,
  TABLE_HEAD,
  TABLE_HEAD_CELL,
  TABLE_HEAD_ROW,
  TABLE_IDENTITY,
  TABLE_LINK,
  TABLE_ROW,
  TABLE_SCROLL,
  TABLE_SECONDARY,
} from '@/components/ui/table-shell';
import { Avatar } from '@/components/ui/Avatar';
import { SearchInput } from '@/components/ui/SearchInput';
import { Tabs, TabPanel, type TabDefinition } from '@/components/ui/Tabs';
import { EmptyState } from '@/components/feedback/EmptyState';
import { ConfirmationModal } from '@/components/ui/ConfirmationModal';
import {
  PlusIcon,
  EditIcon,
  TrashIcon,
  UsersIcon,
  LockIcon,
  FileTextIcon,
  EyeIcon,
} from '@/components/icons';
import { httpClient as http } from '@/lib/api';
import { useAccess, canManageAccess, canDelegateRole, type Overview } from './access';
import './AccessPage.css';

type AccessTab = 'roles' | 'teams' | 'users' | 'audit';
const titles = {
  roles: 'Rôles et permissions',
  teams: 'Équipes',
  users: 'Collaborateurs',
  audit: 'Historique des accès',
};
const descriptions = {
  roles: 'Un rôle définit les données visibles et les actions autorisées.',
  teams: 'Les membres d’une équipe partagent leur portefeuille selon les droits de leur rôle.',
  users: 'Gérez les comptes internes et attribuez leurs accès à la plateforme.',
  audit: 'Retrouvez les 50 dernières modifications des accès et des équipes.',
};
const auditLabels: Record<string, string> = {
  ROLE_CREATED: 'Rôle créé',
  ROLE_UPDATED: 'Permissions modifiées',
  ROLE_DELETED: 'Rôle supprimé',
  TEAM_CREATED: 'Équipe créée',
  TEAM_UPDATED: 'Équipe modifiée',
  TEAM_DELETED: 'Équipe supprimée',
  USER_ROLE_CHANGED: 'Rôle attribué',
  USER_CREATED: 'Utilisateur créé',
};
export const AccessPage = () => {
  const access = useAccess();
  const cache = useQueryClient();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [removing, setRemoving] = useState<{
    type: 'roles' | 'teams';
    id: string;
    name: string;
  } | null>(null);
  const query = useQuery({
    queryKey: ['access-administration'],
    queryFn: () => http.get<Overview>('/access'),
    enabled: canManageAccess(access.data),
  });
  const data = query.data;
  const tabs: TabDefinition<AccessTab>[] = [
    ...(access.can('roles') || access.can('permissions')
      ? [
          {
            value: 'roles' as const,
            label: 'Rôles et permissions',
            icon: <LockIcon />,
            count: data?.roles.length,
          },
        ]
      : []),
    ...(access.can('teams')
      ? [
          {
            value: 'teams' as const,
            label: 'Équipes',
            icon: <UsersIcon />,
            count: data?.teams.length,
          },
        ]
      : []),
    ...(access.can('users')
      ? [
          {
            value: 'users' as const,
            label: 'Collaborateurs',
            icon: <UsersIcon />,
            count: data?.users.length,
          },
        ]
      : []),
    ...(access.can('permissions')
      ? [{ value: 'audit' as const, label: 'Historique', icon: <FileTextIcon /> }]
      : []),
  ];
  const tab =
    tabs.find((item) => item.value === params.get('onglet'))?.value ?? tabs[0]?.value ?? 'roles';
  const resource = tab === 'users' ? 'users' : tab === 'teams' ? 'teams' : 'roles';
  const createPath =
    tab === 'users'
      ? 'collaborateurs/nouveau'
      : tab === 'teams'
        ? 'equipes/nouvelle'
        : 'roles/nouveau';
  const createLabel =
    tab === 'users'
      ? 'Créer un utilisateur'
      : tab === 'teams'
        ? 'Créer une équipe'
        : 'Créer un rôle';
  const matches = (text: string) =>
    text.toLocaleLowerCase('fr').includes(search.trim().toLocaleLowerCase('fr'));
  const remove = useMutation({
    mutationFn: async () => {
      if (removing) await http.delete('/access/' + removing.type + '/' + removing.id);
    },
    onSuccess: async () => {
      setRemoving(null);
      await cache.invalidateQueries({ queryKey: ['access-administration'] });
      await cache.invalidateQueries({ queryKey: ['access-me'] });
    },
  });
  const askRemove = (type: 'roles' | 'teams', id: string, name: string) => {
    remove.reset();
    setRemoving({ type, id, name });
  };
  if (access.isPending) return <p role="status">Chargement des accès…</p>;
  if (!canManageAccess(access.data))
    return <p role="alert">Vous ne disposez pas des droits de gestion des accès.</p>;
  const roles = data?.roles.filter((role) => matches(role.name + ' ' + role.description)) ?? [];
  const teams = data?.teams.filter((team) => matches(team.name + ' ' + team.description)) ?? [];
  const users =
    data?.users.filter((user) =>
      matches(user.firstName + ' ' + user.lastName + ' ' + user.email),
    ) ?? [];
  const empty =
    (tab === 'roles' && !roles.length) ||
    (tab === 'teams' && !teams.length) ||
    (tab === 'users' && !users.length);
  return (
    <div className="access-page">
      <Link to={ROUTES.settings} className="text-ink-500 hover:text-accent-700 text-sm">
        ← Paramètres
      </Link>
      <header className="access-list-heading">
        <div className="access-heading">
          <span className="access-heading-icon" aria-hidden>
            <LockIcon />
          </span>
          <h1>Accès et équipes</h1>
        </div>
        {tab !== 'audit' && access.can(resource, 'create') && (
          <Button onClick={() => void navigate('/parametres/acces/' + createPath)}>
            <PlusIcon />
            {createLabel}
          </Button>
        )}
      </header>
      <Tabs
        id="access-tabs"
        label="Gestion des accès"
        value={tab}
        onChange={(value) => {
          setParams({ onglet: value });
          setSearch('');
        }}
        tabs={tabs}
        className="w-full bg-white/90 p-1.5"
      />
      {query.isError ? (
        <div className="access-error" role="alert">
          {query.error.message} <Button onClick={() => void query.refetch()}>Réessayer</Button>
        </div>
      ) : !data ? (
        <p role="status">Chargement des accès…</p>
      ) : (
        <TabPanel
          key={tab}
          id={'access-tabs-' + tab + '-panel'}
          labelledBy={'access-tabs-' + tab}
          hidden={false}
        >
          <section className="access-directory">
            <header className="access-directory-heading">
              <div>
                <h2>{titles[tab]}</h2>
                <p>{descriptions[tab]}</p>
              </div>
            </header>
            {tab !== 'audit' && (
              <div className="access-directory-search">
                <SearchInput
                  value={search}
                  onValueChange={setSearch}
                  placeholder={
                    tab === 'users'
                      ? 'Rechercher un nom ou une adresse e-mail…'
                      : tab === 'teams'
                        ? 'Rechercher une équipe…'
                        : 'Rechercher un rôle…'
                  }
                  aria-label="Rechercher dans les accès"
                />
              </div>
            )}
            {empty ? (
              <EmptyState
                icon={<UsersIcon />}
                title={search ? 'Aucun résultat' : 'Aucun élément pour le moment'}
                description={
                  search
                    ? 'Essayez un autre nom ou effacez votre recherche.'
                    : 'Utilisez le bouton de création pour commencer.'
                }
              />
            ) : null}
            {tab === 'roles' && roles.length > 0 && (
              <div className={TABLE_SCROLL}>
                <table className={`${TABLE} min-w-[720px]`}>
                  <caption className="sr-only">Rôles et niveaux d’accès aux modules</caption>
                  <thead className={TABLE_HEAD}>
                    <tr className={TABLE_HEAD_ROW}>
                      <th scope="col" className={TABLE_HEAD_CELL}>
                        Rôle
                      </th>
                      <th scope="col" className={TABLE_HEAD_CELL}>
                        Accès aux modules
                      </th>
                      <th scope="col" className={TABLE_HEAD_CELL}>
                        Collaborateurs
                      </th>
                      <th scope="col" className={TABLE_HEAD_CELL}>
                        Type
                      </th>
                      <th scope="col" className={TABLE_HEAD_CELL}>
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className={TABLE_BODY}>
                    {roles.map((role) => {
                      const delegable = canDelegateRole(access.data, role);
                      const editable =
                        delegable &&
                        role.systemKey !== 'ADMIN' &&
                        (access.can('roles', 'update') || access.can('permissions', 'update'));
                      return (
                        <tr key={role.id} className={TABLE_ROW}>
                          <td className={TABLE_CELL}>
                            <div className="access-cell-identity">
                              <span className="access-row-icon">
                                <LockIcon />
                              </span>
                              <div>
                                <Link
                                  to={'/parametres/acces/roles/' + role.id + '/modifier'}
                                  className={TABLE_LINK}
                                >
                                  {role.name}
                                </Link>
                                <small className={`${TABLE_SECONDARY} block`}>
                                  {role.description || 'Rôle personnalisé'}
                                </small>
                              </div>
                            </div>
                          </td>
                          <td className={TABLE_CELL_TEXT}>
                            {role.systemKey === 'ADMIN'
                              ? 'Accès complet'
                              : role.permissions.filter((grant) => grant.visibility !== 'NONE')
                                  .length + ' modules'}
                          </td>
                          <td className={TABLE_CELL}>
                            <span className="access-count">
                              <UsersIcon />
                              {role._count.users}
                            </span>
                          </td>
                          <td className={TABLE_CELL}>
                            <span className="access-pill">
                              {role.systemKey === 'ADMIN'
                                ? 'Protégé'
                                : role.systemKey
                                  ? 'Système'
                                  : 'Personnalisé'}
                            </span>
                          </td>
                          <td className={TABLE_CELL}>
                            <div className="access-row-actions">
                              <ActionIcon
                                label={(editable ? 'Configurer ' : 'Voir ') + role.name}
                                onClick={() =>
                                  void navigate('/parametres/acces/roles/' + role.id + '/modifier')
                                }
                              >
                                {editable ? <EditIcon /> : <EyeIcon />}
                              </ActionIcon>
                              {access.can('roles', 'create') &&
                                access.can('permissions', 'create') &&
                                delegable && (
                                  <ActionIcon
                                    label={'Dupliquer ' + role.name}
                                    onClick={() =>
                                      void navigate(
                                        '/parametres/acces/roles/' + role.id + '/dupliquer',
                                      )
                                    }
                                  >
                                    <PlusIcon />
                                  </ActionIcon>
                                )}
                              {!role.systemKey && delegable && access.can('roles', 'delete') && (
                                <ActionIcon
                                  label={'Supprimer ' + role.name}
                                  onClick={() => askRemove('roles', role.id, role.name)}
                                >
                                  <TrashIcon />
                                </ActionIcon>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {tab === 'teams' && teams.length > 0 && (
              <div className={TABLE_SCROLL}>
                <table className={`${TABLE} min-w-[720px]`}>
                  <caption className="sr-only">Équipes et leurs membres</caption>
                  <thead className={TABLE_HEAD}>
                    <tr className={TABLE_HEAD_ROW}>
                      <th scope="col" className={TABLE_HEAD_CELL}>
                        Équipe
                      </th>
                      <th scope="col" className={TABLE_HEAD_CELL}>
                        Membres
                      </th>
                      <th scope="col" className={TABLE_HEAD_CELL}>
                        Mise à jour
                      </th>
                      <th scope="col" className={TABLE_HEAD_CELL}>
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className={TABLE_BODY}>
                    {teams.map((team) => (
                      <tr key={team.id} className={TABLE_ROW}>
                        <td className={TABLE_CELL}>
                          <div className="access-cell-identity">
                            <span className="access-row-icon">
                              <UsersIcon />
                            </span>
                            <div>
                              <Link
                                to={'/parametres/acces/equipes/' + team.id + '/modifier'}
                                className={TABLE_LINK}
                              >
                                {team.name}
                              </Link>
                              <small className={`${TABLE_SECONDARY} block`}>
                                {team.description || 'Portefeuille partagé'}
                              </small>
                            </div>
                          </div>
                        </td>
                        <td className={TABLE_CELL}>
                          <div className="access-team-avatars">
                            {team.members.slice(0, 4).map((member) => {
                              const user = data.users.find((item) => item.id === member.userId);
                              return user ? (
                                <Avatar
                                  key={user.id}
                                  name={user.firstName + ' ' + user.lastName}
                                  src={user.avatarUrl}
                                />
                              ) : null;
                            })}
                            <span className="access-count">
                              {team.members.length} membre{team.members.length > 1 ? 's' : ''}
                            </span>
                          </div>
                        </td>
                        <td className={`${TABLE_CELL_TEXT} whitespace-nowrap`}>
                          {new Date(team.updatedAt).toLocaleDateString('fr-FR')}
                        </td>
                        <td className={TABLE_CELL}>
                          <div className="access-row-actions">
                            <ActionIcon
                              label={
                                (access.can('teams', 'update') ? 'Modifier ' : 'Voir ') + team.name
                              }
                              onClick={() =>
                                void navigate('/parametres/acces/equipes/' + team.id + '/modifier')
                              }
                            >
                              {access.can('teams', 'update') ? <EditIcon /> : <EyeIcon />}
                            </ActionIcon>
                            {access.can('teams', 'delete') && (
                              <ActionIcon
                                label={'Supprimer ' + team.name}
                                onClick={() => askRemove('teams', team.id, team.name)}
                              >
                                <TrashIcon />
                              </ActionIcon>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {tab === 'users' && users.length > 0 && (
              <div className={TABLE_SCROLL}>
                <table className={`${TABLE} min-w-[720px]`}>
                  <caption className="sr-only">
                    Collaborateurs internes, leur rôle et leurs équipes
                  </caption>
                  <thead className={TABLE_HEAD}>
                    <tr className={TABLE_HEAD_ROW}>
                      <th scope="col" className={TABLE_HEAD_CELL}>
                        Collaborateur
                      </th>
                      <th scope="col" className={TABLE_HEAD_CELL}>
                        Rôle
                      </th>
                      <th scope="col" className={TABLE_HEAD_CELL}>
                        Équipes
                      </th>
                      <th scope="col" className={TABLE_HEAD_CELL}>
                        Statut
                      </th>
                      <th scope="col" className={TABLE_HEAD_CELL}>
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className={TABLE_BODY}>
                    {users.map((user) => {
                      const role = data.roles.find((item) => item.id === user.accessRoleId);
                      return (
                        <tr key={user.id} className={TABLE_ROW}>
                          <td className={TABLE_CELL}>
                            <div className="access-cell-identity">
                              <Avatar
                                name={user.firstName + ' ' + user.lastName}
                                src={user.avatarUrl}
                              />
                              <div>
                                <span className={TABLE_IDENTITY}>
                                  {user.firstName} {user.lastName}
                                </span>
                                <small className={`${TABLE_SECONDARY} block`}>{user.email}</small>
                              </div>
                            </div>
                          </td>
                          <td className={TABLE_CELL_TEXT}>{role?.name ?? 'Aucun accès'}</td>
                          <td className={TABLE_CELL}>
                            <div className="access-tags">
                              {user.teamMemberships.map(({ teamId }) => (
                                <span key={teamId}>
                                  {data.teams.find((team) => team.id === teamId)?.name}
                                </span>
                              ))}
                              {!user.teamMemberships.length && (
                                <span className="access-muted">Aucune équipe</span>
                              )}
                            </div>
                          </td>
                          <td className={TABLE_CELL}>
                            <span
                              className={
                                'access-status access-status--' + user.status.toLowerCase()
                              }
                            >
                              {
                                (
                                  {
                                    ACTIVE: 'Actif',
                                    PENDING: 'En attente',
                                    SUSPENDED: 'Suspendu',
                                  } as Record<string, string>
                                )[user.status]
                              }
                            </span>
                          </td>
                          <td className={TABLE_CELL}>
                            {access.can('users', 'update') &&
                              access.can('permissions', 'update') &&
                              (!role || canDelegateRole(access.data, role)) && (
                                <ActionIcon
                                  label={'Modifier le rôle de ' + user.firstName}
                                  onClick={() =>
                                    void navigate(
                                      '/parametres/acces/collaborateurs/' + user.id + '/role',
                                    )
                                  }
                                >
                                  <EditIcon />
                                </ActionIcon>
                              )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {tab === 'audit' && (
              <div className="access-audit">
                {!data.audit.length && (
                  <EmptyState
                    icon={<FileTextIcon />}
                    title="Aucune modification"
                    description="Les prochaines modifications apparaîtront ici."
                  />
                )}
                {data.audit.map((entry) => {
                  const actor = data.users.find((user) => user.id === entry.actorId);
                  const target =
                    data.roles.find((role) => role.id === entry.targetId)?.name ??
                    data.teams.find((team) => team.id === entry.targetId)?.name ??
                    data.users.find((user) => user.id === entry.targetId)?.email;
                  return (
                    <div key={entry.id}>
                      <span className="access-audit-dot" />
                      <div>
                        <strong>{auditLabels[entry.action] ?? entry.action}</strong>
                        <p>
                          {target ? target + ' · ' : ''}
                          {actor ? actor.firstName + ' ' + actor.lastName : 'Collaborateur interne'}
                        </p>
                      </div>
                      <time dateTime={entry.createdAt}>
                        {new Date(entry.createdAt).toLocaleString('fr-FR')}
                      </time>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </TabPanel>
      )}
      {removing && (
        <ConfirmationModal
          description={
            'Supprimer « ' +
            removing.name +
            ' » ? ' +
            (removing.type === 'teams'
              ? 'Les membres conserveront leurs rôles mais perdront le partage lié à cette équipe.'
              : 'Ce rôle ne doit plus être attribué.')
          }
          pending={remove.isPending}
          error={remove.error?.message}
          onConfirm={() => remove.mutate()}
          onCancel={() => setRemoving(null)}
        />
      )}
    </div>
  );
};
